import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { areEqual } from 'Utils/equalityChecks';

import { getJobProfitability, getCostLines, getJobCostingReportBlob } from '../actions';
import type { CostLineQuery } from '../actions';
import type {
  JobProfitability,
  CostLine,
  PaginationMeta,
  CostCategory,
} from '../types';
import {
  COST_CATEGORIES,
  COST_CATEGORY_LABELS,
  COST_SOURCE_LABELS,
} from '../types';
import {
  formatCurrency,
  formatLineAmount,
  formatMargin,
  formatSignedCurrency,
  formatDate,
  buildGhlOpportunityUrl,
} from '../format';
import { Pagination } from '../Pagination';
import { CostBreakdownChart } from './CostBreakdownChart';
import { JobBudgetEditor } from './JobBudgetEditor';
import classes from '../jobCosting.module.css';

interface Props {
  ssoToken: string;
  locationId: string;
  opportunityId: string;
  jobName: string | null;
  onBack: () => void;
}

const EMPTY_META: PaginationMeta = { currentPage: 1, lastPage: 1, perPage: 100, total: 0 };

const matchBadgeClass = (confidence: string | null): string => {
  switch (confidence) {
    case 'exact':
      return classes.badgeExact;
    case 'fuzzy':
      return classes.badgeFuzzy;
    case 'unmatched':
      return classes.badgeUnmatched;
    default:
      return classes.badgeNeutral;
  }
};

// Variance colour without nesting ternaries (eslint no-nested-ternary).
const varianceClass = (hasBudget: boolean, variance: number): string => {
  if (!hasBudget) return '';
  return variance >= 0 ? classes.profitPositive : classes.profitNegative;
};

const categoryLabel = (c: string): string =>
  COST_CATEGORY_LABELS[c as CostCategory] || c;
const sourceLabel = (s: string): string =>
  COST_SOURCE_LABELS[s as keyof typeof COST_SOURCE_LABELS] || s;

function JobCostingDetail({ ssoToken, locationId, opportunityId, jobName, onBack }: Props) {
  const [summary, setSummary] = useState<JobProfitability | null>(null);
  const [lines, setLines] = useState<CostLine[]>([]);
  const [meta, setMeta] = useState<PaginationMeta>(EMPTY_META);
  const [category, setCategory] = useState('');
  const [source, setSource] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const loadSummary = useCallback(() => {
    getJobProfitability(ssoToken, opportunityId)
      .then((s) => setSummary(s))
      .catch(() => {
        /* summary is best-effort; the cost-lines table is the primary content */
      });
  }, [ssoToken, opportunityId]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const fetchLines = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query: CostLineQuery = {
        costCategory: category || undefined,
        source: source || undefined,
        page,
      };
      const result = await getCostLines(ssoToken, opportunityId, query);
      setLines(result.data);
      setMeta(result.meta);
    } catch {
      setError('Unable to load cost lines. Please try again.');
      setLines([]);
      setMeta(EMPTY_META);
    } finally {
      setLoading(false);
    }
  }, [ssoToken, opportunityId, category, source, page]);

  useEffect(() => {
    fetchLines();
  }, [fetchLines]);

  const handleExportPdf = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    setExportError(null);
    try {
      const blob = await getJobCostingReportBlob(ssoToken, opportunityId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (summary?.rpJobNumber ?? opportunityId).replace(/\./g, '_');
      a.download = `job-costing-${safeName}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setExportError('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [ssoToken, opportunityId, summary, exporting]);

  // Group the current page by category so each category's lines are followed by its subtotal.
  const groupedByCategory = useMemo(() => {
    const groups = new Map<string, { lines: CostLine[]; subtotal: number }>();
    lines.forEach((l) => {
      const group = groups.get(l.costCategory) || { lines: [], subtotal: 0 };
      group.lines.push(l);
      group.subtotal += Number(l.amount ?? 0);
      groups.set(l.costCategory, group);
    });
    return Array.from(groups.entries());
  }, [lines]);

  const renderCostLines = () => {
    if (loading) {
      return (
        <div className={classes.loadingState}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading cost lines...</p>
        </div>
      );
    }
    if (lines.length === 0) {
      return <div className={classes.emptyState}>No cost lines recorded for this job yet.</div>;
    }
    return (
      <>
        <div className={classes.tableWrapper}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Category</th>
                <th>Source</th>
                <th>Vendor</th>
                <th>Description</th>
                <th className={classes.num}>Amount</th>
                <th>Match</th>
              </tr>
            </thead>
            <tbody>
              {groupedByCategory.map(([cat, group]) => (
                <React.Fragment key={cat}>
                  {group.lines.map((l) => (
                    <tr key={l.id}>
                      <td>{formatDate(l.txnDate)}</td>
                      <td>{categoryLabel(l.costCategory)}</td>
                      <td>{sourceLabel(l.source)}</td>
                      <td>{l.vendorName || '—'}</td>
                      <td>{l.description || '—'}</td>
                      <td className={classes.num}>{formatLineAmount(l.amount)}</td>
                      <td>
                        <span className={`${classes.badge} ${matchBadgeClass(l.matchConfidence)}`}>
                          {l.matchConfidence || 'n/a'}
                        </span>
                      </td>
                    </tr>
                  ))}
                  <tr className={classes.totalsRow}>
                    <td colSpan={5}>{`${categoryLabel(cat)} subtotal`}</td>
                    <td className={classes.num} colSpan={2}>
                      {formatLineAmount(group.subtotal)}
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination meta={meta} onPageChange={setPage} />
      </>
    );
  };

  return (
    <div>
      <button type="button" className={classes.backButton} onClick={onBack}>
        &larr; Back to all jobs
      </button>

      <div className={classes.header}>
        <h4 className="mb-0">
          {summary?.name || jobName || 'Job'}
          <a
            href={buildGhlOpportunityUrl(locationId, opportunityId)}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.externalLink}
            title="Open opportunity in RocketCRM"
          >
            ↗ Open in RocketCRM
          </a>
          <button
            type="button"
            className={classes.exportButton}
            onClick={handleExportPdf}
            disabled={!summary || exporting}
            title="Export PDF"
          >
            {exporting ? 'Exporting…' : 'Export PDF'}
          </button>
        </h4>
      </div>

      {exportError && <div className={`alert alert-danger ${classes.emptyState}`}>{exportError}</div>}

      {summary && (
        <>
          <div className={classes.summaryCards}>
            <div className={classes.summaryCard}>
              <div className={classes.summaryLabel}>Revenue</div>
              <div className={classes.summaryValue}>{formatCurrency(summary.revenue)}</div>
            </div>
            <div className={classes.summaryCard}>
              <div className={classes.summaryLabel}>Total Cost</div>
              <div className={classes.summaryValue}>{formatCurrency(summary.totalCost)}</div>
            </div>
            <div className={classes.summaryCard}>
              <div className={classes.summaryLabel}>Gross Profit</div>
              <div
                className={`${classes.summaryValue} ${summary.grossProfit >= 0 ? classes.profitPositive : classes.profitNegative}`}
              >
                {formatCurrency(summary.grossProfit)}
              </div>
            </div>
            <div className={classes.summaryCard}>
              <div className={classes.summaryLabel}>Gross Margin</div>
              <div className={classes.summaryValue}>{formatMargin(summary.grossMarginPct)}</div>
            </div>
            <div className={classes.summaryCard}>
              <div className={classes.summaryLabel}>Budget</div>
              <div className={classes.summaryValue}>
                {summary.hasBudget ? formatCurrency(summary.totalBudget) : '—'}
              </div>
            </div>
            <div className={classes.summaryCard}>
              <div className={classes.summaryLabel}>Budget Variance</div>
              <div
                className={`${classes.summaryValue} ${varianceClass(summary.hasBudget, summary.budgetVariance)}`}
              >
                {summary.hasBudget ? formatSignedCurrency(summary.budgetVariance) : '—'}
              </div>
            </div>
          </div>
          <CostBreakdownChart costsByCategory={summary.costsByCategory} />

          <div className={classes.header}>
            <h5 className="mb-0">Budget vs. Actual</h5>
          </div>
          <JobBudgetEditor
            ssoToken={ssoToken}
            opportunityId={opportunityId}
            costsByCategory={summary.costsByCategory}
            onSaved={loadSummary}
          />
        </>
      )}

      <div className={classes.toolbar}>
        <div className={classes.field}>
          <span className={classes.fieldLabel}>Category</span>
          <select
            className={classes.select}
            value={category}
            onChange={(e) => {
              setPage(1);
              setCategory(e.target.value);
            }}
          >
            <option value="">All categories</option>
            {COST_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {COST_CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        </div>
        <div className={classes.field}>
          <span className={classes.fieldLabel}>Source</span>
          <select
            className={classes.select}
            value={source}
            onChange={(e) => {
              setPage(1);
              setSource(e.target.value);
            }}
          >
            <option value="">All sources</option>
            {Object.entries(COST_SOURCE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className={`alert alert-danger ${classes.emptyState}`}>{error}</div>}

      {renderCostLines()}
    </div>
  );
}

const JobCostingDetailMemo = memo(JobCostingDetail, areEqual);

export { JobCostingDetailMemo as JobCostingDetail };
